import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Download,
  Mail,
  Phone,
  User
} from 'lucide-react';
import { generateMembersPdf } from '../utils/pdfGenerator';
import MemberFormModal from './MemberFormModal';
import MemberProfileModal from './MemberProfileModal'; // Importa el modal

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const offset = d.getTimezoneOffset();
  d.setMinutes(d.getMinutes() - offset);
  return d.toISOString().slice(0, 10);
}

const Members = () => {
  // Estado para paginación
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [members, setMembers] = useState([]);
  const [memberToEdit, setMemberToEdit] = useState(null);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [totalSocios, setTotalSocios] = useState(0);
  // Cargar miembros desde la base de datos, incluyendo el último pago
  const fetchMembers = () => {
  fetch('https://chungwhav2.tnf.cl/api/miembros-con-ultimo-pago')
      .then(res => res.json())
      .then(data => setMembers(Array.isArray(data) ? data : []));
  };

  const handleViewMember = (member) => {
    setSelectedMember(member);
    setShowMemberModal(true);
  };

  useEffect(() => {
  fetchMembers();
  fetch('https://chungwhav2.tnf.cl/api/miembros-total')
      .then(res => res.json())
      .then(data => {
        setTotalSocios(data.total);
        console.log('Total socios desde BD:', data.total);
      })
      .catch(err => {
        console.error('Error al obtener total de socios:', err);
        setTotalSocios(0);
      });
  }, []);

  const getMembershipBadge = (type) => {
    const badges = {
      basic: 'bg-blue-100 text-blue-800',
      premium: 'bg-purple-100 text-purple-800',
      vip: 'bg-yellow-100 text-yellow-800'
    };
    return badges[type] || badges.basic;
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return badges[status] || badges.pending;
  };

  const filteredMembers = members.filter(member => {
  const matchesSearch = member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
             member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
             member.rut?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || member.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const handleExportPdf = () => {
    generateMembersPdf(filteredMembers);
  };

  const handleAddMember = () => {
    setMemberToEdit(null);
    setShowAddModal(true);
  };

  const handleEditMember = (member) => {
    setMemberToEdit({
      ...member,
      joinDate: formatDate(member.joinDate),
      birthDate: formatDate(member.birthDate)
      // lastPayment NO se edita manualmente
    });
    setShowAddModal(true);
  };

  const handleDeleteMember = (id) => {
    if (window.confirm('¿Está seguro de que desea eliminar este socio?')) {
  fetch(`https://chungwhav2.tnf.cl/api/miembros/${id}`, {
        method: 'DELETE'
      })
        .then(res => res.json())
        .then(() => fetchMembers());
    }
  };

  const handleSaveMember = (newMemberData) => {
  fetch('https://chungwhav2.tnf.cl/api/miembros', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newMemberData)
    })
      .then(res => res.json())
      .then(() => {
        setShowAddModal(false);
        fetchMembers();
      });
  };

  return (
    <div className="space-y-8">
      
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
            Gestión de Socios
          </h1>
          <p className="text-gray-600">Administre la información de los socios de su organización</p>
        </div>
        
        <motion.button
          onClick={handleAddMember}
          className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus className="w-5 h-5" />
          Agregar Socio
        </motion.button>
      </motion.div>
 {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: 'Total de Socios', value: totalSocios, color: 'from-blue-500 to-cyan-500' },
          { label: 'Socios Activos', value: members.filter(m => m.status === 'active').length, color: 'from-green-500 to-emerald-500' },
          { label: 'Socios Expirados', value: members.filter(m => m.status === 'expired').length, color: 'from-red-500 to-pink-500' },
          { label: 'Socios VIP', value: members.filter(m => m.membershipType === 'vip').length, color: 'from-yellow-500 to-orange-500' }
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-6 shadow-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 + index * 0.1 }}
          >
            <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mb-4`}>
              <Users className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
            <p className="text-gray-600 font-medium">{stat.label}</p>
          </motion.div>
        ))}
      </div>
      {/* Filters and Search */}
      <motion.div
        className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-6 shadow-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, rut o correo electrónico..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
            />
          </div>
          
          <div className="flex gap-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
            >
              <option value="all">Todos los estados</option>
              <option value="active">Activo</option>
              <option value="expired">Expirado</option>
              <option value="pending">Pendiente</option>
            </select>
            
            <motion.button
              onClick={handleExportPdf}
              className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors duration-200 flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Download className="w-5 h-5" />
              Exportar PDF
            </motion.button>
          </div>
        </div>
      </motion.div>
{/* Filtro de cantidad de resultados y paginación */}
      <div className="flex items-center gap-4 mb-2">
        <label className="text-sm text-gray-700">Mostrar:</label>
        <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }} className="px-2 py-1 border rounded">
          {[10,20,50,100,500,1000].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <button onClick={() => setPage(p => Math.max(p-1,0))} className="px-2 py-1 border rounded bg-gray-100 hover:bg-gray-200">&lt;</button>
        <span className="text-sm">Página {page+1} de {Math.max(1, Math.ceil(filteredMembers.length/rowsPerPage))}</span>
        <button onClick={() => setPage(p => Math.min(p+1, Math.ceil(filteredMembers.length/rowsPerPage)-1))} className="px-2 py-1 border rounded bg-gray-100 hover:bg-gray-200">&gt;</button>
      </div>
      {/* Members Table */}
      <motion.div
        className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Información del Socio</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Contacto</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Tipo de Membresía</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Estado</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Fecha de Ingreso</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Último Pago</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredMembers.slice(page*rowsPerPage, (page+1)*rowsPerPage).map((member, index) => (
                <motion.tr
                  key={member.id}
                  className="hover:bg-gray-50/50 transition-colors duration-200"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.05 }}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {member.name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{member.name}</p>
                        <p className="text-sm text-gray-500">RUT: {member.rut || '-'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4" />
                        {member.email}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4" />
                        {member.phone}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getMembershipBadge(member.membershipType)}`}>
                      {member.membershipType === 'basic' ? 'Básica' :
                       member.membershipType === 'premium' ? 'Premium' : 'VIP'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(member.status)}`}>
                      {member.status === 'active' ? 'Activo' :
                       member.status === 'expired' ? 'Expirado' : 'Pendiente'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(member.joinDate)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(member.lastPayment)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <motion.button
      onClick={() => handleViewMember(member)}
  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
  whileHover={{ scale: 1.1 }}
  whileTap={{ scale: 0.9 }}
  title="Ver ficha del socio"
    >
      <User className="w-4 h-4" />
    </motion.button>
                      <motion.button
                        onClick={() => handleEditMember(member)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Edit className="w-4 h-4" />
                      </motion.button>
                      <motion.button
                        onClick={() => handleDeleteMember(member.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </motion.button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

     

      <MemberFormModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveMember}
        memberToEdit={memberToEdit}
      />
      <MemberProfileModal
  isOpen={showMemberModal}
  onClose={() => setShowMemberModal(false)}
  member={selectedMember}
/>
    </div>
  );
};

export default Members;